import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  iif,
  Observable,
  of,
  merge,
  Subject,
  fromEvent,
  Subscriber,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  tap,
} from 'rxjs/operators';
import { TableCaptionDirective } from '../directives/table-caption.directive';
import { TableCheckboxColumnDirective } from '../directives/table-checkbox-column.directive';
import { TableColumnDirective } from '../directives/table-column.directive';
import { TableExpandableRowDirective } from '../directives/table-expandable-row.directive';
import { HorizontalScroll } from '../domain/horizontal-scroll';
import { TableDataTransform } from '../domain/table-data-transform';
import { TableSelectionState } from '../domain/table-selection-state';
import { TableState } from '../domain/table-state';
import {
  TableElement,
  TableElementKey,
  InfiniteScrollOptions,
  TableBorder,
  TableColumnEvent,
  TableStateEvent,
} from '../domain/types';

@Component({
  selector: 'ps-ui-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T extends Record<string, unknown>>
  implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy
{
  @Input()
  public set data(elements: T[]) {
    this.dataCommand$.next(elements);
  }

  @Input()
  public scroll: InfiniteScrollOptions = { distance: 2, throttle: 20 };

  @Input()
  public border: TableBorder = 'bottom';

  @Input()
  public trackBy?: keyof T;

  @Input()
  public set state(event: TableStateEvent) {
    this.stateInputCommand$.next(event);
  }

  @Input()
  public headless = false;

  @Output()
  public scrolled = new EventEmitter();

  @Output()
  public stateChange = new EventEmitter<TableStateEvent>();

  @ContentChildren(TableColumnDirective)
  public _columns!: QueryList<TableColumnDirective>;

  @ContentChild(TableCheckboxColumnDirective, { static: true })
  public checkbox?: TableCheckboxColumnDirective<T>;

  @ContentChild(TableExpandableRowDirective)
  public expandable?: TableExpandableRowDirective;

  @ContentChild(TableCaptionDirective)
  public caption?: TableCaptionDirective;

  @ViewChild('tableContainer', { static: true })
  public container: ElementRef<HTMLDivElement>;

  public get columns() {
    return this.expandable?.columns ?? this._columns;
  }

  public get footer() {
    return this.columns.some((column) => !!column.footer);
  }

  public elements$!: Observable<TableElement<T>[]>;
  public allSelected = false;
  public state$!: Observable<TableState>;
  public scrollLeft$!: Observable<boolean>;
  public scrollRight$!: Observable<boolean>;
  public overflowed$!: Observable<boolean>;

  private readonly overflowCommand$ = new Subject();
  private readonly scrollCommand$ = new Subject<'left' | 'right'>();
  private readonly dataCommand$ = new BehaviorSubject<T[]>([]);
  private readonly stateCommand$ = new Subject<TableColumnEvent>();
  private readonly stateInputCommand$ = new BehaviorSubject<TableStateEvent>({
    sort: { field: '', type: 'none' },
    filter: [],
  });
  private readonly columnsChangeCommand$ = new Subject<boolean>();
  private readonly _subscriptions = new Subscriber();

  private _keys: TableElementKey<T>[] = [];

  public ngOnInit(): void {
    this.state$ = merge(
      this.stateCommand$.pipe(
        scan((state, event) => state.update(event), TableState.empty()),
        tap((state) => this.stateChange.emit(state.event()))
      ),
      combineLatest([
        this.stateInputCommand$,
        this.columnsChangeCommand$.pipe(startWith(null)),
      ]).pipe(
        map(([event]) => event),
        map((event) => TableState.from(event, this.columns.toArray()))
      )
    ).pipe(shareReplay(1));

    const selecteds$ = iif(
      () => !!this.checkbox,
      this.checkbox?.selected$,
      of(TableSelectionState.none<T>())
    );

    this.elements$ = combineLatest([
      this.dataCommand$,
      selecteds$,
      this.state$,
    ]).pipe(
      tap(([data, selected]) => {
        this.allSelected = data.length === selected.current().length;
      }),
      map(
        ([data, selecteds, state]) =>
          new TableDataTransform(data, selecteds, state, this.trackBy)
      ),
      map((state) => state.elements()),
      tap((elements) => {
        this._keys = elements.map((element) => element.key);
      })
    );

    this._scrollEvents();
  }

  public ngAfterViewInit() {
    // We need to subscribe here because on OnInit columns is undefined
    // Query objects are seted before ngAfterViewInit call
    this._subscriptions.add(
      this.columns.changes.subscribe(() => {
        this.columnsChangeCommand$.next(true);
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public ngAfterViewChecked(): void {
    this.overflowCommand$.next();
  }

  public onElementSelected(element: TableElement<T>): void {
    this.checkbox?.select(element.key);
  }

  public onSelectAll(event: Event): void {
    const checked = (<HTMLInputElement>event.target).checked;
    this.checkbox?.select(checked && !this.allSelected ? this._keys : []);
  }

  public onScroll() {
    this.scrolled.emit();
  }

  public onScrollLeft() {
    this.scrollCommand$.next('left');
  }

  public onScrollRight() {
    this.scrollCommand$.next('right');
  }

  public onStateChange(event: TableColumnEvent): void {
    this.stateCommand$.next(event);
  }

  public trackByProperty(
    _: number,
    element: TableElement<T>
  ): number | T[keyof T] {
    return element.trackBy;
  }

  private _scrollEvents(): void {
    const overflow$ = this.overflowCommand$.pipe(
      filter(() => !!this.container),
      map(() => this._keys.length),
      distinctUntilChanged(),
      map(() => new HorizontalScroll(this.container)),
      filter((scroll) => scroll.overflowed()),
      shareReplay()
    );

    const nativeScroll$ = fromEvent(
      this.container.nativeElement,
      'scroll'
    ).pipe(
      map(() => new HorizontalScroll(this.container)),
      shareReplay()
    );

    const left$ = this.scrollCommand$.pipe(
      filter((type) => type === 'left'),
      map(() => new HorizontalScroll(this.container)),
      tap((scroll) => scroll.scrollLeft())
    );

    const right$ = this.scrollCommand$.pipe(
      filter((type) => type === 'right'),
      map(() => new HorizontalScroll(this.container)),
      tap((scroll) => scroll.scrollRight())
    );

    const scroll$ = merge(left$, right$).pipe(shareReplay());

    this.scrollLeft$ = merge(overflow$, scroll$, nativeScroll$).pipe(
      map((scroll) => scroll.canScrollLeft()),
      debounceTime(50)
    );

    this.scrollRight$ = merge(overflow$, scroll$, nativeScroll$).pipe(
      map((scroll) => scroll.canScrollRight()),
      debounceTime(50)
    );

    this.overflowed$ = overflow$.pipe(map((scroll) => scroll.overflowed()));
  }
}
